import {
  type FocusEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '../../lib/cn';
import { computePosition, type Placement, Portal, rectOf } from './layer';

let lastHiddenAt = 0;

type Options = { placement?: Placement; delay?: number; describe?: boolean; disabled?: boolean };

/**
 * Підказка для елемента: з'являється після затримки при наведенні мишею і одразу — при фокусі з клавіатури.
 * Якщо щойно була відкрита інша підказка, наступна з'являється без затримки й анімації.
 */
export function useTooltip(content: ReactNode, { placement = 'bottom', delay = 450, describe = true, disabled }: Options = {}) {
  const id = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [instant, setInstant] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback(
    (el: HTMLElement, immediate: boolean) => {
      if (disabled || !content) return;
      window.clearTimeout(timer.current);
      const warm = Date.now() - lastHiddenAt < 400;
      if (immediate || warm) {
        setInstant(warm);
        setAnchor(el);
      } else {
        timer.current = window.setTimeout(() => {
          setInstant(false);
          setAnchor(el);
        }, delay);
      }
    },
    [content, delay, disabled],
  );

  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setAnchor((a) => {
      if (a) lastHiddenAt = Date.now();
      return null;
    });
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const triggerProps = {
    onPointerEnter: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType === 'mouse') show(e.currentTarget, false);
    },
    onPointerLeave: hide,
    onPointerDown: hide,
    onFocus: (e: FocusEvent<HTMLElement>) => {
      const t = e.target as HTMLElement;
      if (t.matches?.(':focus-visible')) show(e.currentTarget, true);
    },
    onBlur: hide,
    'aria-describedby': describe && anchor ? id : undefined,
  };

  const element = anchor ? <TooltipBubble id={id} anchor={anchor} placement={placement} instant={instant} content={content} onDismiss={hide} /> : null;
  return { triggerProps, element };
}

function TooltipBubble({
  id,
  anchor,
  placement,
  instant,
  content,
  onDismiss,
}: {
  id: string;
  anchor: HTMLElement;
  placement: Placement;
  instant: boolean;
  content: ReactNode;
  onDismiss: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; origin: string } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchor.isConnected) return;
    const p = computePosition(rectOf(anchor), { width: el.offsetWidth, height: el.offsetHeight }, placement, 6);
    setPos({ top: p.top, left: p.left, origin: p.origin });
  }, [anchor, placement, content]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    const onScroll = () => onDismiss();
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onDismiss]);
  return (
    <Portal>
      <div
        ref={ref}
        id={id}
        role="tooltip"
        className={cn(
          'pointer-events-none fixed z-[70] max-w-72 rounded-xs bg-fg px-2 py-1 text-caption text-fg-inverse',
          !instant && 'ers-pop',
          !pos && 'invisible',
        )}
        style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, ['--ers-origin' as string]: pos?.origin }}
      >
        {content}
      </div>
    </Portal>
  );
}

/** Обгортка для простих випадків: <Tip content="…"><button …/></Tip>. */
export function Tip({ content, children, placement, className }: { content: ReactNode; children: ReactNode; placement?: Placement; className?: string }) {
  const { triggerProps, element } = useTooltip(content, { placement });
  return (
    <span className={cn('inline-flex', className)} {...triggerProps}>
      {children}
      {element}
    </span>
  );
}
